import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { confirmDialog } from "../../../dialogs";
import {
  fetchMessages,
  searchMessagesApi,
  sendMessageApi,
  sendAttachmentApi,
  sendVoiceApi,
  deleteMessageApi,
  editMessageApi,
  reactToMessageApi,
  markThreadMessagesRead,
  loadConversationsData,
  sendTypingApi,
  type FetchMessagesResult,
  type PendingSendPayload,
} from "../../../chatApi";
import { dateLabelFor, initials, activityHintDismissed, dismissActivityHint } from "./chatShared";
import { AvatarImage } from "../../components/AvatarImage";
import { MessageBubble } from "./MessageBubble";
import { MessageActionsPopover } from "./MessageActionsPopover";
import { ChatMediaViewerDialog } from "./ChatMediaViewerDialog";
import { ChatMediaDialog } from "./ChatMediaDialog";
import { VoiceRecorderBar } from "./VoiceRecorderBar";
import { ActivityLauncherSheet } from "./ActivityLauncherSheet";
import type { ChatMessage, ChatReplyTo } from "../../../types";

// Search-as-you-type debounce - short enough to still feel live, long
// enough that a fast typist doesn't fire a decrypt-and-scan request (see
// server.js's search route comment) per keystroke.
const SEARCH_DEBOUNCE_MS = 350;
// How long a jumped-to message stays visually flagged after scrolling to
// it - long enough to register, short enough not to linger.
const HIGHLIGHT_DURATION_MS = 2000;
// Infinite-scroll-down mirrors the existing infinite-scroll-up threshold.
const SCROLL_LOAD_THRESHOLD_PX = 200;

const CHAT_TEXTAREA_MAX_HEIGHT_PX = 120;
const LONG_PRESS_MS = 500;

interface PopoverState {
  message: ChatMessage;
  anchorBubble: HTMLElement;
}

// Fabricates a locally-rendered ChatMessage before any network round-trip -
// senderId/senderName are placeholders (own-ness is decided by senderEmail
// elsewhere in this file, and neither field is ever displayed for a
// message the current user sent) since this client has no real user id
// available, and both are discarded the moment the message reconciles with
// the server's real response in settleSend() below.
function buildOptimisticMessage(
  conversationId: string,
  payload: PendingSendPayload,
  localId: string,
  senderEmail: string,
  replyTo: ChatReplyTo | null,
): ChatMessage {
  let attachment: ChatMessage["attachment"] = null;
  if (payload.kind === "attachment" && payload.file) {
    const isVideo = /\.(mp4|mov)$/i.test(payload.file.name);
    attachment = {
      filename: payload.file.name,
      type: isVideo ? "video" : "image",
      thumbFilename: null,
      size: payload.file.size,
      localBlobUrl: URL.createObjectURL(payload.file),
    };
  } else if (payload.kind === "voice" && payload.file) {
    attachment = {
      filename: payload.file.name,
      type: "audio",
      thumbFilename: null,
      size: payload.file.size,
      durationSec: payload.durationSec,
      waveform: payload.waveform,
      localBlobUrl: URL.createObjectURL(payload.file),
    };
  }

  return {
    id: localId,
    conversationId,
    senderId: senderEmail,
    senderEmail,
    senderName: senderEmail,
    text: payload.kind === "text" ? (payload.text ?? null) : null,
    attachment,
    // An optimistic send is always a normal text/attachment message - a
    // Private Drop card is server-created only (POST /api/gifts), never
    // composed by the user, so this is always null here.
    card: null,
    replyTo,
    reactions: [],
    createdAt: new Date().toISOString(),
    deliveredAt: null,
    readAt: null,
    deletedAt: null,
    editedAt: null,
    localId,
    pending: true,
    failed: false,
  };
}

// Portals into #chatThreadView wholesale (header, messages, reply preview,
// composer) - everything here is dynamic, so there's no static content
// worth preserving in
// index.html. Visibility (#chatThreadView's d-none) is driven by
// react-router (ChatThreadRouteScreen.tsx toggles it on route mount/
// unmount), same as the list screen.
function ChatThreadBody() {
  const navigate = useNavigate();
  const threadId = useAppState((s) => s.chatThreadId);
  const peerTyping = useAppState((s) => s.typingConversationId) === threadId;
  const messages = useAppState((s) => s.chatThreadMessages);
  const conversation = useAppState((s) => s.chatConversations.find((c) => c.id === s.chatThreadId));
  const currentUserEmail = useAppState((s) => s.currentUserEmail);
  const isMine = (message: ChatMessage): boolean => message.senderEmail === currentUserEmail;
  // ChatConversationSummary.otherUser only ever carries {email, fullName} -
  // avatar/online-status live in the separate presence directory
  // (PresenceIndicator.tsx/CoupleHeader.tsx's own source), cross-referenced
  // by email since this app has no other stable shared identifier exposed
  // to the client for this. A 2-person app never has ambiguity here.
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const partnerPresence = conversation ? presenceUsers.find((p) => p.email === conversation.otherUser.email) ?? null : null;

  // Tracks which thread's messages are actually loaded and rendered, distinct
  // from `messages` itself - showing "Loading..." must never depend on
  // `messages` still holding the *previous* thread's content for one frame
  // while this thread's fetch is in flight (a real flash the original
  // avoided by synchronously writing "Loading..." before the fetch started).
  const [loadedThreadId, setLoadedThreadId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Whether the loaded window has more history beyond either edge (lives in
  // appState, not local state - see state.ts's own comment on why
  // ChatScreen.tsx's WS handler needs to read chatThreadHasMoreNewer too),
  // and whether a page in either direction is being fetched right now (the
  // scroll-near-edge triggers below must not fire a second request while
  // one's already in flight).
  const hasMoreOlder = useAppState((s) => s.chatThreadHasMoreOlder);
  const hasMoreNewer = useAppState((s) => s.chatThreadHasMoreNewer);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const [pendingReplyTo, setPendingReplyTo] = useState<ChatReplyTo | null>(null);
  // Contextual reply (V2 Part E) - GiftViewerDialog's Reply button lands
  // here with appState.chatPendingReplyTo already populated (see
  // ChatThreadRouteScreen.tsx). Consumed once per thread visit, then
  // cleared so it doesn't reapply on a later normal visit to this thread.
  const chatPendingReplyTo = useAppState((s) => s.chatPendingReplyTo);
  useEffect(() => {
    if (!chatPendingReplyTo || appState.chatThreadId !== threadId) return;
    setPendingReplyTo(chatPendingReplyTo);
    appState.chatPendingReplyTo = null;
    notifyStateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatPendingReplyTo, threadId]);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [text, setText] = useState("");
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [voiceActive, setVoiceActive] = useState(false);
  const [viewingImageSrc, setViewingImageSrc] = useState<string | null>(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);
  const [activitySheetOpen, setActivitySheetOpen] = useState(false);
  // First-use discovery hint for the ✨ button - read once on mount (not
  // reactive to the dismiss happening elsewhere), shown until the user
  // actually opens the launcher once, matching installPrompt.ts's own
  // "seen it once, never again" convention (see chatShared.ts).
  const [showActivityHint, setShowActivityHint] = useState(() => !activityHintDismissed());

  // Search Mode - replaces the normal header (see the render section) with
  // a back arrow + inline text field + calendar button, matching the
  // WhatsApp reference. searchResults is kept ascending (oldest first) so
  // "next" reads as "more recent" - the server's own search route returns
  // newest-first, reversed once client-side (see the debounced effect
  // below); no server change needed just to flip an array.
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([]);
  const [searchResultIndex, setSearchResultIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressBubble = useRef<HTMLElement | null>(null);
  // Whether the server currently thinks WE are typing (so a typing_started
  // is only sent once per burst of keystrokes, not on every single one) and
  // the debounce timer that sends typing_stopped after a pause.
  const amTypingRef = useRef(false);
  const typingStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Scoped to this component's lifetime (lost on navigating away and back,
  // same as `text`/other transient composer state) - a failed send can be
  // retried while the thread stays open, not recovered after leaving it.
  const pendingRetryData = useRef<Map<string, PendingSendPayload & { conversationId: string }>>(new Map());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards a slower earlier search response from landing after a faster
  // later one and flashing stale results over the current query's real ones.
  const searchTokenRef = useRef(0);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scrollToBottom(): void {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  // Continuous/infinite-scroll history: triggered by scrolling near the
  // top of the list (see handleMessagesScroll below) rather than a "Load
  // more" button, matching WhatsApp/Messenger. Prepending older messages
  // above the current viewport would otherwise yank the scroll position
  // down to wherever the now-taller content pushes it - scrollHeight/
  // scrollTop are captured before the fetch and restored (relative to the
  // NEW scrollHeight) once the taller content has actually painted, so the
  // message the user was looking at stays exactly in place.
  async function loadOlderMessages(): Promise<void> {
    if (!threadId || loadingOlder || !hasMoreOlder) return;
    const oldest = messages[0];
    if (!oldest) return;

    setLoadingOlder(true);
    const el = messagesRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    try {
      const result = await fetchMessages(threadId, { before: oldest.id });
      if (!result || appState.chatThreadId !== threadId) return; // navigated away meanwhile, or a 401
      appState.chatThreadMessages = [...result.messages, ...appState.chatThreadMessages];
      appState.chatThreadHasMoreOlder = result.hasMoreOlder;
      notifyStateChange();
      requestAnimationFrame(() => {
        const el2 = messagesRef.current;
        if (el2) el2.scrollTop = el2.scrollHeight - prevScrollHeight + prevScrollTop;
      });
    } finally {
      setLoadingOlder(false);
    }
  }

  // The mirror of loadOlderMessages, for scrolling back down toward the
  // present after having jumped to a search result/date in the middle of
  // history (see jumpToMessage/jumpToDate below) - appending at the bottom
  // needs no scroll-position correction, unlike prepending at the top.
  async function loadNewerMessages(): Promise<void> {
    if (!threadId || loadingNewer || !hasMoreNewer) return;
    const newest = messages[messages.length - 1];
    if (!newest) return;

    setLoadingNewer(true);
    try {
      const result = await fetchMessages(threadId, { after: newest.id });
      if (!result || appState.chatThreadId !== threadId) return;
      appState.chatThreadMessages = [...appState.chatThreadMessages, ...result.messages];
      appState.chatThreadHasMoreNewer = result.hasMoreNewer;
      notifyStateChange();
    } finally {
      setLoadingNewer(false);
    }
  }

  function handleMessagesScroll(e: React.UIEvent<HTMLDivElement>): void {
    cancelLongPress();
    const el = e.currentTarget;
    if (el.scrollTop < SCROLL_LOAD_THRESHOLD_PX) void loadOlderMessages();
    else if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_THRESHOLD_PX) void loadNewerMessages();
  }

  // Fetches the live tail (the normal "just opened this thread" view) and
  // applies it - factored out so both the mount effect below and
  // closeSearch/the popstate handler (returning to normal chat) share one
  // implementation instead of two copies that could drift. Returns whether
  // it actually applied anything, so callers can tell "the thread changed
  // out from under us mid-fetch" apart from a real success.
  async function loadLatestMessages(forThreadId: string): Promise<boolean> {
    const result = await fetchMessages(forThreadId);
    if (!result || appState.chatThreadId !== forThreadId) return false;
    appState.chatThreadMessages = result.messages;
    appState.chatThreadHasMoreOlder = result.hasMoreOlder;
    appState.chatThreadHasMoreNewer = result.hasMoreNewer;
    notifyStateChange();
    setLoadedThreadId(forThreadId);
    requestAnimationFrame(scrollToBottom);
    markThreadMessagesRead(forThreadId);
    return true;
  }

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    setLoadError(false);
    setPendingReplyTo(null);
    setText("");
    setPopover(null);
    setLoadingOlder(false);
    setLoadingNewer(false);
    // Switching threads always exits Search Mode - a search/jump result
    // makes no sense carried over to a different conversation.
    setSearchMode(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearchResultIndex(-1);
    setHighlightedMessageId(null);
    appState.chatThreadHasMoreOlder = false;
    appState.chatThreadHasMoreNewer = false;

    (async () => {
      try {
        await loadLatestMessages(threadId);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Tells the OTHER side "I stopped" when navigating away from this thread
  // (or closing the tab) mid-keystroke - closes over THIS render's threadId
  // so switching threads notifies the conversation being LEFT, not the one
  // being entered.
  useEffect(() => {
    return () => {
      if (typingStopTimer.current) {
        clearTimeout(typingStopTimer.current);
        typingStopTimer.current = null;
      }
      if (amTypingRef.current && threadId) {
        amTypingRef.current = false;
        sendTypingApi(threadId, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // --- Search Mode ---

  // Shared tail for both jumpToMessage (a search result) and jumpToDate (a
  // calendar pick): replace the loaded window with the server's around-
  // centered page, flag the target message so it flashes briefly, and
  // scroll it into view once the new (possibly much shorter/taller) content
  // has actually painted.
  function applyJumpResult(result: FetchMessagesResult, messageId: string): void {
    appState.chatThreadMessages = result.messages;
    appState.chatThreadHasMoreOlder = result.hasMoreOlder;
    appState.chatThreadHasMoreNewer = result.hasMoreNewer;
    notifyStateChange();
    setLoadedThreadId(threadId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setHighlightedMessageId(messageId);
    highlightTimerRef.current = setTimeout(() => setHighlightedMessageId(null), HIGHLIGHT_DURATION_MS);
    requestAnimationFrame(() => {
      messagesRef.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`)?.scrollIntoView({ block: "center" });
    });
  }

  async function jumpToMessage(messageId: string): Promise<void> {
    if (!threadId) return;
    const result = await fetchMessages(threadId, { around: messageId });
    if (!result || appState.chatThreadId !== threadId) return;
    applyJumpResult(result, messageId);
  }

  async function jumpToDate(dateStr: string): Promise<void> {
    if (!threadId) return;
    const result = await fetchMessages(threadId, { date: dateStr });
    if (!result || appState.chatThreadId !== threadId) return;
    if (!result.anchorMessageId) {
      showToast("No messages found.", "info");
      return;
    }
    applyJumpResult(result, result.anchorMessageId);
  }

  function goToSearchResult(index: number): void {
    if (index < 0 || index >= searchResults.length) return;
    setSearchResultIndex(index);
    void jumpToMessage(searchResults[index].id);
  }

  // Chevron-up/down: up = older (toward the start of the array), down =
  // more recent - matches the WhatsApp reference's up/down result nav.
  function handlePrevResult(): void {
    goToSearchResult(searchResultIndex - 1);
  }
  function handleNextResult(): void {
    goToSearchResult(searchResultIndex + 1);
  }

  // Debounced search-as-you-type. Auto-jumps to the most recent match as
  // soon as results arrive (a reasonable default - the most recent hit is
  // usually the relevant one), leaving prev/next to page toward older ones.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!searchMode || !threadId || !searchQuery.trim()) {
      setSearchResults([]);
      setSearchResultIndex(-1);
      setSearching(false);
      return;
    }
    setSearching(true);
    const token = ++searchTokenRef.current;
    searchDebounceRef.current = setTimeout(async () => {
      const found = await searchMessagesApi(threadId, searchQuery);
      if (searchTokenRef.current !== token) return; // a newer query already superseded this one
      const ascending = [...found].reverse(); // server returns newest-first
      setSearchResults(ascending);
      setSearching(false);
      if (ascending.length) {
        const lastIdx = ascending.length - 1;
        setSearchResultIndex(lastIdx);
        void jumpToMessage(ascending[lastIdx].id);
      } else {
        setSearchResultIndex(-1);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchMode, threadId]);

  function openSearch(): void {
    setMoreMenuAnchor(null);
    setSearchMode(true);
    setSearchQuery("");
    setSearchResults([]);
    setSearchResultIndex(-1);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }

  // Just requests history back - the popstate handler below (registered
  // only while searchMode is true) does the actual state reset + reload,
  // so both a tap on the back arrow and a real hardware/browser Back press
  // converge on exactly one code path instead of two that could drift out
  // of sync with each other.
  function closeSearch(): void {
    window.history.back();
  }

  function openDatePicker(): void {
    const el = dateInputRef.current;
    if (!el) return;
    // showPicker() is the modern API (Chrome/Android WebView, which is what
    // this app's Android TWA runs on) - falls back to a plain click for
    // browsers without it, which still opens the native date UI just via a
    // slightly different code path.
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  }

  // Hardware/browser Back while Search Mode is active should close Search
  // Mode first, not leave the conversation - "Search Mode -> Normal Chat ->
  // Previous Screen", matching the WhatsApp reference. Nothing else in this
  // app intercepts history/popstate today; this pushes and owns exactly one
  // synthetic entry, only while searchMode is true, so it can't accumulate
  // across repeated open/close cycles or fight react-router's own listener
  // (the pushed entry doesn't change the URL, only its state object, so
  // react-router sees a same-pathname no-op).
  //
  // Known limitation: switching conversations (or unmounting) while Search
  // Mode is active resets searchMode directly, without popping this
  // synthetic entry first (calling history.back() there would just as
  // likely undo the real navigation that triggered the switch). That can
  // leave one harmless stray forward-entry in the browser history stack in
  // that specific combo - at most one extra Back press ever needed to
  // leave, never a lost navigation or a crash.
  useEffect(() => {
    if (!searchMode) return;
    window.history.pushState({ chatSearch: true }, "");
    const onPopState = () => {
      setSearchMode(false);
      setSearchQuery("");
      setSearchResults([]);
      setSearchResultIndex(-1);
      setHighlightedMessageId(null);
      if (threadId) void loadLatestMessages(threadId);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode]);

  function autoGrow(): void {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, CHAT_TEXTAREA_MAX_HEIGHT_PX)}px`;
  }

  // Sends typing_started at most once per burst of keystrokes (not on every
  // one), then typing_stopped once 2s pass with no further input - the
  // debounce the plan called for. Cleared immediately (not left to the
  // debounce) whenever the field goes empty or the message actually sends.
  function stopTyping(): void {
    if (typingStopTimer.current) {
      clearTimeout(typingStopTimer.current);
      typingStopTimer.current = null;
    }
    if (amTypingRef.current && threadId) {
      amTypingRef.current = false;
      sendTypingApi(threadId, false);
    }
  }

  function handleTextChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    const value = e.target.value;
    setText(value);
    autoGrow();

    if (!threadId) return;
    if (!value.trim()) {
      stopTyping();
      return;
    }
    if (!amTypingRef.current) {
      amTypingRef.current = true;
      sendTypingApi(threadId, true);
    }
    if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(stopTyping, 2000);
  }

  // A <textarea> doesn't submit its form on Enter by default - Enter sends
  // (matching WhatsApp Web), Shift+Enter inserts a newline.
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  }

  // Shared by text/attachment/voice sends: insert a locally-rendered
  // "sending" bubble immediately, then reconcile it with the server's real
  // message (or flip it to a failed/retryable state) once the request
  // settles. Nothing renders a message only-after-the-server-confirms
  // anymore, unlike this component's original always-await-then-commit
  // behavior.
  async function sendOptimistic(payload: PendingSendPayload, replyTo: ChatReplyTo | null): Promise<void> {
    const conversationId = threadId;
    if (!conversationId) return;
    const localId = crypto.randomUUID();
    pendingRetryData.current.set(localId, { ...payload, conversationId });

    const optimistic = buildOptimisticMessage(conversationId, payload, localId, currentUserEmail, replyTo);
    appState.chatThreadMessages = [...appState.chatThreadMessages, optimistic];
    notifyStateChange();
    requestAnimationFrame(scrollToBottom);

    await settleSend(localId, payload, conversationId);
  }

  async function settleSend(localId: string, payload: PendingSendPayload, conversationId: string): Promise<void> {
    const result =
      payload.kind === "text"
        ? await sendMessageApi(conversationId, payload.text!, payload.replyToId)
        : payload.kind === "attachment"
          ? await sendAttachmentApi(conversationId, payload.file!, payload.replyToId)
          : await sendVoiceApi(conversationId, payload.file!, payload.durationSec!, payload.waveform!, payload.replyToId);

    if (appState.chatThreadId !== conversationId) return; // navigated away meanwhile
    if (!result.ok) {
      if (result.reason === "error") {
        appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
          m.localId === localId ? { ...m, pending: false, failed: true } : m,
        );
        notifyStateChange();
      }
      // "unauthorized": requestLock() already fired inside the *Api call - leave the bubble pending, app is locking anyway.
      return;
    }

    pendingRetryData.current.delete(localId);
    const localBlobUrl = appState.chatThreadMessages.find((m) => m.localId === localId)?.attachment?.localBlobUrl;
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) => (m.localId === localId ? { ...result.data, localId } : m));
    notifyStateChange();
    if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    loadConversationsData();
  }

  function retrySend(localId: string): void {
    const payload = pendingRetryData.current.get(localId);
    if (!payload) return;
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
      m.localId === localId ? { ...m, pending: true, failed: false } : m,
    );
    notifyStateChange();
    void settleSend(localId, payload, payload.conversationId);
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !threadId) return;

    if (editingMessage) {
      await saveEdit(editingMessage.id, trimmed);
      return;
    }

    const replyToId = pendingReplyTo?.messageId;
    const replyTo = pendingReplyTo;
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setPendingReplyTo(null);
    stopTyping();

    await sendOptimistic({ kind: "text", text: trimmed, replyToId }, replyTo);
  }

  function requestEditMessage(message: ChatMessage): void {
    setPendingReplyTo(null);
    setEditingMessage(message);
    setText(message.text || "");
    requestAnimationFrame(() => {
      autoGrow();
      textareaRef.current?.focus();
    });
  }

  function cancelEdit(): void {
    setEditingMessage(null);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }

  async function saveEdit(messageId: string, newText: string): Promise<void> {
    if (!threadId) return;
    const result = await editMessageApi(threadId, messageId, newText);
    if (!result.ok) {
      if (result.reason === "error") showToast(result.error || "Failed to save edit.", "error");
      return;
    }
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) => (m.id === messageId ? result.data : m));
    notifyStateChange();
    setEditingMessage(null);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }

  async function handleAttachChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    // Shared by the gallery-attach input and the camera-capture input below
    // - reset whichever one actually fired, not a hardcoded ref, so both
    // stay able to re-select/re-capture the same filename consecutively.
    e.target.value = "";
    if (!file || !threadId) return;

    const isVideo = /\.(mp4|mov)$/i.test(file.name);
    const maxSize = isVideo ? 500 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast(`File too large (max ${isVideo ? "500MB for videos" : "10MB for photos"}).`, "error");
      return;
    }

    const replyToId = pendingReplyTo?.messageId;
    const replyTo = pendingReplyTo;
    setPendingReplyTo(null);

    await sendOptimistic({ kind: "attachment", file, replyToId }, replyTo);
  }

  function handleVoiceSend(file: File, durationSec: number, waveform: number[]): void {
    if (!threadId) return;
    const replyToId = pendingReplyTo?.messageId;
    const replyTo = pendingReplyTo;
    setPendingReplyTo(null);
    void sendOptimistic({ kind: "voice", file, durationSec, waveform, replyToId }, replyTo);
  }

  async function requestDeleteMessage(messageId: string): Promise<void> {
    const conversationId = threadId;
    if (!conversationId) return;

    const ok = await confirmDialog({ title: "Delete Message", message: "Delete this message?", confirmLabel: "Delete" });
    if (!ok) return;

    const result = await deleteMessageApi(conversationId, messageId);
    if (!result.ok) {
      if (result.reason === "error") showToast("Failed to delete message.", "error");
      return;
    }
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
      m.id === messageId ? { ...m, text: null, attachment: null, deletedAt: new Date().toISOString() } : m,
    );
    notifyStateChange();
    loadConversationsData();
  }

  async function setMyReaction(messageId: string, emoji: string | null): Promise<void> {
    const conversationId = threadId;
    if (!conversationId) return;

    const result = await reactToMessageApi(conversationId, messageId, emoji);
    if (!result.ok) {
      if (result.reason === "error") showToast("Failed to react.", "error");
      return;
    }
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
      m.id === messageId ? { ...m, reactions: result.data } : m,
    );
    notifyStateChange();
  }

  function cancelLongPress(): void {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressBubble.current?.classList.remove("is-pressing");
    longPressBubble.current = null;
  }

  // Pointer Events cover touch and mouse uniformly - matches WhatsApp's
  // hold-a-message pattern (react/reply/delete) instead of a permanently
  // visible trash icon.
  function handleMessagesPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    const bubble = (e.target as HTMLElement).closest<HTMLElement>(".chat-bubble:not(.is-deleted)");
    if (!bubble) return;

    longPressBubble.current = bubble;
    bubble.classList.add("is-pressing");
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      const target = longPressBubble.current;
      longPressBubble.current = null;
      bubble.classList.remove("is-pressing");
      const messageId = target?.dataset.messageId;
      const message = messageId ? messages.find((m) => m.id === messageId) : undefined;
      if (target && message) setPopover({ message, anchorBubble: target });
    }, LONG_PRESS_MS);
  }

  function renderMessages(): ReactNode {
    if (loadError) return <p className="small muted text-center mb-0">Failed to load messages.</p>;
    if (loadedThreadId !== threadId) return <p className="small muted text-center mb-0">Loading…</p>;
    if (!messages.length) return <p className="small muted text-center mb-0">No messages yet. Say hello!</p>;

    let lastDateLabel = "";
    // Tracks whether the previous row was sent by the same side (own vs
    // other) so consecutive messages from one person can sit close
    // together while a sender switch gets real breathing room - reset to
    // null across a date separator so the first message of a new day never
    // reads as "grouped" with the last message of the previous one.
    let lastOwn: boolean | null = null;
    const nodes: ReactNode[] = [];
    for (const message of messages) {
      const label = dateLabelFor(message.createdAt);
      if (label !== lastDateLabel) {
        nodes.push(
          <div key={`sep-${message.id}`} className="chat-date-separator">
            <span>{label}</span>
          </div>,
        );
        lastDateLabel = label;
        lastOwn = null;
      }
      const own = isMine(message);
      nodes.push(
        <MessageBubble
          key={message.id}
          message={message}
          own={own}
          grouped={lastOwn === own}
          highlighted={message.id === highlightedMessageId}
          currentUserEmail={currentUserEmail}
          onRetry={retrySend}
          onOpenImage={setViewingImageSrc}
          onReact={setMyReaction}
        />,
      );
      lastOwn = own;
    }
    return nodes;
  }

  const container = getVanillaEl<HTMLDivElement>("chatThreadView");
  if (!container || !threadId) return null;

  const name = conversation ? conversation.otherUser.fullName || conversation.otherUser.email : "Chat";
  const hasText = text.trim().length > 0;
  const myReaction = popover?.message.reactions.find((r) => r.userEmail === currentUserEmail)?.emoji;

  return createPortal(
    <>
      {searchMode ? (
        <div className="chat-thread-header chat-search-header d-flex align-items-center gap-1 mb-2">
          <IconButton aria-label="Back" onClick={closeSearch} size="small">
            <i className="bi bi-arrow-left" aria-hidden="true" />
          </IconButton>
          <input
            ref={searchInputRef}
            type="text"
            className="chat-search-input flex-grow-1 min-w-0"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search messages in this conversation"
          />
          <IconButton aria-label="Search by date" title="Search by date" onClick={openDatePicker} size="small">
            <i className="bi bi-calendar3" aria-hidden="true" />
          </IconButton>
          {/* Hidden but not display:none/unmounted - showPicker()/click()
              need a real, rendered form control to act on. The native OS
              date picker (see the WhatsApp reference screenshots - that IS
              a stock Android date dialog) opens straight from this, no
              custom calendar UI to build/maintain. */}
          <input
            ref={dateInputRef}
            type="date"
            className="chat-search-date-input"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => {
              if (e.target.value) void jumpToDate(e.target.value);
              e.target.value = "";
            }}
          />
        </div>
      ) : (
        <div className="chat-thread-header d-flex align-items-center gap-2">
          <button type="button" className="chat-thread-header-icon-btn" aria-label="Back to chats" onClick={() => navigate("/chat")}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
          </button>
          <span className="chat-avatar flex-shrink-0" aria-hidden="true">
            {partnerPresence?.avatarFilename ? (
              <AvatarImage
                filename={partnerPresence.avatarFilename}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                fallback={initials(name)}
              />
            ) : (
              initials(name)
            )}
            {partnerPresence && (
              <span
                className={`chat-thread-status-dot${partnerPresence.online ? " is-online" : ""}`}
                aria-hidden="true"
              />
            )}
          </span>
          <div className="chat-thread-header-titles flex-grow-1 min-w-0">
            <h2 className="chat-thread-header-name">{name}</h2>
            {/* Only shown once the presence directory actually has this
                person - never fabricated ("Online"/"Offline" text with no
                real signal behind it would be worse than omitting it). */}
            {partnerPresence && (
              <span className="chat-thread-header-status">{partnerPresence.online ? "Online" : "Offline"}</span>
            )}
          </div>
          {/* Voice/video calling isn't a feature this app has - these stay
              as an honest "not available yet" toast rather than either a
              fake working button or omitting a control the layout calls
              for entirely. */}
          <button
            type="button"
            className="chat-thread-header-icon-btn"
            aria-label="Voice call"
            onClick={() => showToast("Voice calls aren't available yet.", "info")}
          >
            <i className="bi bi-telephone" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chat-thread-header-icon-btn"
            aria-label="Video call"
            onClick={() => showToast("Video calls aren't available yet.", "info")}
          >
            <i className="bi bi-camera-video" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chat-thread-header-icon-btn"
            aria-label="More options"
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
          >
            <i className="bi bi-three-dots-vertical" aria-hidden="true" />
          </button>
          <Menu anchorEl={moreMenuAnchor} open={!!moreMenuAnchor} onClose={() => setMoreMenuAnchor(null)}>
            <MenuItem onClick={openSearch}>
              <ListItemIcon>
                <i className="bi bi-search" aria-hidden="true" />
              </ListItemIcon>
              Search
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMoreMenuAnchor(null);
                setMediaOpen(true);
              }}
            >
              <ListItemIcon>
                <i className="bi bi-images" aria-hidden="true" />
              </ListItemIcon>
              Shared Media
            </MenuItem>
          </Menu>
        </div>
      )}

      {searchMode && searchQuery.trim() && (
        <div className="chat-search-status d-flex align-items-center justify-content-between small muted mb-2">
          {searching ? (
            <span>Searching…</span>
          ) : searchResults.length ? (
            <>
              <span>
                {searchResultIndex + 1} of {searchResults.length}
              </span>
              <span className="d-flex gap-1">
                <IconButton aria-label="Previous result" size="small" onClick={handlePrevResult} disabled={searchResultIndex <= 0}>
                  <i className="bi bi-chevron-up" aria-hidden="true" />
                </IconButton>
                <IconButton
                  aria-label="Next result"
                  size="small"
                  onClick={handleNextResult}
                  disabled={searchResultIndex >= searchResults.length - 1}
                >
                  <i className="bi bi-chevron-down" aria-hidden="true" />
                </IconButton>
              </span>
            </>
          ) : (
            <span>No results</span>
          )}
        </div>
      )}

      <div
        ref={messagesRef}
        className="chat-messages"
        role="log"
        aria-label="Messages"
        aria-live="polite"
        onPointerDown={handleMessagesPointerDown}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onScroll={handleMessagesScroll}
      >
        {loadingOlder && <p className="small muted text-center mb-0">Loading earlier messages…</p>}
        {renderMessages()}
        {loadingNewer && <p className="small muted text-center mb-0">Loading more recent messages…</p>}
        {peerTyping && (
          // Wrapped in .chat-message-row like every real message now - a
          // bare .chat-bubble on its own has no alignment of its own
          // anymore (that moved to the row wrapper when the quick-heart/
          // outside-meta redesign landed), so without this the typing
          // bubble would silently stop aligning to the left.
          <div className="chat-message-row is-other">
            <div className="chat-bubble is-other chat-typing-bubble" aria-live="polite">
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
            </div>
          </div>
        )}
      </div>

      {pendingReplyTo && (
        <div className="chat-reply-preview mt-3">
          <div className="chat-reply-preview-body min-w-0 flex-grow-1">
            <div className="chat-reply-preview-sender small fw-semibold">{pendingReplyTo.senderName}</div>
            <div className="chat-reply-preview-snippet small muted text-truncate">{pendingReplyTo.snippet}</div>
          </div>
          <button type="button" className="btn-close" aria-label="Cancel reply" onClick={() => setPendingReplyTo(null)} />
        </div>
      )}

      {editingMessage && (
        <div className="chat-reply-preview mt-3">
          <div className="chat-reply-preview-body min-w-0 flex-grow-1">
            <div className="chat-reply-preview-sender small fw-semibold">
              <i className="bi bi-pencil me-1" aria-hidden="true" /> Editing message
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Cancel edit" onClick={cancelEdit} />
        </div>
      )}

      <form ref={formRef} className="chat-input-row" onSubmit={handleSubmit} noValidate>
        {!voiceActive && (
          <div className="chat-composer-pill">
            <label htmlFor="chatAttachInput" className="visually-hidden">
              Attach photo or video
            </label>
            <input
              ref={attachInputRef}
              id="chatAttachInput"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
              hidden
              onChange={handleAttachChange}
            />
            {/* Attach/camera/star visually belong to the same rounded pill
                as the text field (the reference's composer is one
                container, not standalone circles flanking it) - only mic/
                send stays outside as its own trailing circle. */}
            <button
              type="button"
              className="chat-pill-icon-btn flex-shrink-0"
              aria-label="Attach photo or video"
              title="Attach"
              onClick={() => attachInputRef.current?.click()}
            >
              <i className="bi bi-paperclip" aria-hidden="true" />
            </button>
            <label htmlFor="chatCameraInput" className="visually-hidden">
              Take a photo
            </label>
            {/* capture="environment" opens the device's native camera app
                directly (rear camera) instead of a gallery/file chooser -
                the returned File flows through the exact same
                handleAttachChange/sendOptimistic path as a picked
                attachment, so no separate upload logic is needed. No new
                Android permission or APK change required: capture is
                delegated to the OS camera app, not an in-page
                getUserMedia video stream. */}
            <input
              ref={cameraInputRef}
              id="chatCameraInput"
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={handleAttachChange}
            />
            <button
              type="button"
              className="chat-pill-icon-btn flex-shrink-0"
              aria-label="Take a photo"
              title="Camera"
              onClick={() => cameraInputRef.current?.click()}
            >
              <i className="bi bi-camera-fill" aria-hidden="true" />
            </button>
            {/* Entry point for this app's synchronized couple experiences
                (Watch Together/Game/Listen Together/Couple Wheel) reachable
                from inside a conversation without leaving it - previously a
                plain MUI dropdown menu, now a real premium row list
                (ActivityLauncherSheet.tsx). Kept visually distinct from the
                plain paperclip/camera icons beside it (filled pink circle,
                not just a tinted-on-hover transparent one) even while
                living inside the same pill - it represents a distinctive
                product capability, not just another attachment action. */}
            <span className="chat-activity-btn-wrap">
              {showActivityHint && (
                <span className="chat-activity-hint" role="status">
                  Things to do together
                </span>
              )}
              <button
                type="button"
                className="chat-activity-btn flex-shrink-0"
                aria-label="Things to do together"
                title="Watch Together, play a game, and more"
                onClick={() => {
                  navigator.vibrate?.(12);
                  if (showActivityHint) {
                    setShowActivityHint(false);
                    dismissActivityHint();
                  }
                  setActivitySheetOpen(true);
                }}
              >
                <i className="bi bi-stars" aria-hidden="true" />
              </button>
            </span>
            <label htmlFor="chatTextInput" className="visually-hidden">
              Message
            </label>
            <textarea
              ref={textareaRef}
              id="chatTextInput"
              className="chat-composer-textarea"
              placeholder="Message..."
              maxLength={4000}
              autoComplete="off"
              rows={1}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
            />
          </div>
        )}
        {/* Exactly one of send/mic shows at a time, like WhatsApp: send once
            there's text to send, the record-voice entry point otherwise.
            VoiceRecorderBar stays mounted unconditionally even while its
            own mic button is hidden - see the hideIdleMic prop and the
            comment on micHidden inside VoiceRecorderBar for why it must
            never unmount mid-gesture. */}
        {!voiceActive && hasText && (
          <button type="submit" className="chat-round-btn chat-round-btn-filled flex-shrink-0" aria-label="Send message" title="Send">
            <i className="bi bi-send-fill" aria-hidden="true" />
          </button>
        )}
        <VoiceRecorderBar onSend={handleVoiceSend} onActiveChange={setVoiceActive} hideIdleMic={hasText} />
      </form>

      {popover && (
        <MessageActionsPopover
          message={popover.message}
          anchorBubble={popover.anchorBubble}
          isMine={isMine(popover.message)}
          myReaction={myReaction}
          onClose={() => setPopover(null)}
          onReply={() => {
            const message = popover.message;
            setPopover(null);
            setPendingReplyTo({
              messageId: message.id,
              senderName: message.senderName,
              snippet:
                message.text ||
                (message.attachment
                  ? message.attachment.type === "video"
                    ? "[Video]"
                    : message.attachment.type === "audio"
                      ? "[Voice message]"
                      : "[Photo]"
                  : ""),
            });
            textareaRef.current?.focus();
          }}
          onEdit={() => {
            const message = popover.message;
            setPopover(null);
            requestEditMessage(message);
          }}
          onDelete={() => {
            const messageId = popover.message.id;
            setPopover(null);
            void requestDeleteMessage(messageId);
          }}
          onReact={(emoji) => {
            const messageId = popover.message.id;
            setPopover(null);
            void setMyReaction(messageId, emoji);
          }}
        />
      )}

      {viewingImageSrc && <ChatMediaViewerDialog src={viewingImageSrc} onClose={() => setViewingImageSrc(null)} />}
      <ChatMediaDialog open={mediaOpen} onClose={() => setMediaOpen(false)} conversationId={threadId} />
      <ActivityLauncherSheet open={activitySheetOpen} onClose={() => setActivitySheetOpen(false)} />
    </>,
    container,
  );
}

export function ChatThreadScreen() {
  return <ChatThreadBody />;
}
